import React from 'react';
import { CircleQuestionMark, CircleArrowLeft, CircleArrowRight } from "lucide-react";
import { ICON_SIZE } from "components/common/constants";
import { Tooltip } from "components/tooltip/Tooltip";
import {handleError} from "utils/errorHandle.function";
import Confirm from "../popup/ConfirmActionPopup.component";
import translate from "../translations/translations.wrapper.jsx";
import {SortableContainer, SortableElement, SortableHandle} from 'react-sortable-hoc';
import * as _ from 'lodash';
import SettingsButtonsBarView from "components/settings/SettingsButtonsBarView.component";
import {getCostUnitTypesVersion} from "components/store/application.reducers";
import {connect} from "react-redux";
import { MODAL_ACTIONS } from "utils/constants";
import { launchDarkly } from "utils/launchDarkly";
import { useModalContext } from "components/modals/ModalContext";

export class CostUnitTypes extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            showSavePopup: false,
            warningMessage: '',
            saveDisabled: false,
            costUnitModified: false
        };

        this.confirmSave = this.confirmSave.bind(this);
        this.onClickSave = this.onClickSave.bind(this);
        this.hideSavePopup = this.hideSavePopup.bind(this);
        this.onSortEnd = this.onSortEnd.bind(this);
        this.selectCostUnitItem = this.selectCostUnitItem.bind(this);
        this.unselectCostUnitItem = this.unselectCostUnitItem.bind(this);
    }

    hideSavePopup() {
        this.setState({showSavePopup: false})
    }

    onClickSave() {
        const data = {
            costUnits: this.props.selectedCostUnitTypes,
            confirm: false
        };
        if (this.state.saveDisabled) {
            return;
        }
        this.setState({saveDisabled: true});
        this.props.setCostUnitsData(data, this.props.costUnitTypesVersion).then(() => {
                this.setState({
                    saveDisabled: false
                });
                this.props.costUnitTypesSaved();
                this.props.getCostUnitsData();
            },
            (error) => {
                if (this.props.useNewModals && this.props.toggleModal) {
                    this.setState({warningMessage: error.errorMessages, saveDisabled: false});
                    this.props.toggleModal(MODAL_ACTIONS.CONFIRM_ACTION, {
                        message: error.errorMessages,
                        handleAction: () => {
                            this.props.toggleModal(null);
                            this.confirmSave();
                        },
                    });
                } else {
                    this.setState({
                        showSavePopup: true,
                        warningMessage: error.errorMessages,
                        saveDisabled: false
                    });
                }
            });
    }

    confirmSave() {
        const data = {
            costUnits: this.props.selectedCostUnitTypes,
            confirm: true
        };

        this.props.setCostUnitsData(data, this.props.costUnitTypesVersion).then(() => {
            this.props.costUnitTypesSaved();
            this.props.getCostUnitsData();
            this.hideSavePopup();
        }, error => {
            handleError(error);
            this.hideSavePopup();
        });
    }

    renderCurrentlySelectedPanel() {
        return (
            this.props.selectedCostUnitTypes.length === 0 ?
                <p>{this.props.translate("costUnitTypes.noItemSelected")}</p> :
                this.props.selectedCostUnitTypes.map((item, index) => {
                    return index === this.props.selectedCostUnitTypes.length - 1 && index !== 0 ? null :
                        <div key={item.index}>
                            {index === 0 ?
                                <p className="mb-2">{this.props.translate("costUnitTypes.firstItemSelected") + item.name + ' (' + item.dimension + ')'}</p> : null}
                            {this.props.selectedCostUnitTypes.length > 1 ?
                                <p className="mb-2">{item.name + ' (' + item.dimension + ')' + this.props.translate("costUnitTypes.currentSelection") + this.props.selectedCostUnitTypes[index + 1].name + ' (' + this.props.selectedCostUnitTypes[index + 1].dimension + ')'}</p> : null}
                        </div>;
                })
        );
    }

    selectCostUnitItem(item) {
        this.props.costUnitTypeSelected(item);
    }

    unselectCostUnitItem(item) {
        this.props.costUnitTypeUnselected(item);
    }

    onSortEnd(event) {
        this.props.costUnitTypeReordered({
            oldIndex: event.oldIndex,
            newIndex: event.newIndex
        });
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (!_.isEqual(prevProps.selectedCostUnitTypes, this.props.selectedCostUnitTypes) ||
            !_.isEqual(prevProps.initialSelectedCostUnitTypes, this.props.initialSelectedCostUnitTypes)) {
            this.setState({
                costUnitModified: !_.isEqual(this.props.selectedCostUnitTypes, this.props.initialSelectedCostUnitTypes),
            });
        }
    }

    render() {
        return (
            <div>
                {this.state.showSavePopup && !this.props.useNewModals &&
                    <Confirm translate={this.props.translate}
                             message={this.state.warningMessage}
                             handleAction={this.confirmSave}
                             closeCallback={this.hideSavePopup}
                    />}
                <h3 className="no-border d-flex align-items-center pt-4">{this.props.translate("costUnitTypes.header")}
                    <span className="ms-4"
                          data-for="cost-unit-types-header-info-tooltip" data-tip="">
                        <CircleQuestionMark size={ICON_SIZE.MEDIUM}/>
                    </span>
                    <Tooltip
                        id="cost-unit-types-header-info-tooltip"
                        content={() => this.props.translate("costUnitTypes.headerInfo")}
                    />
                </h3>
                <div className="row" id="cost-unit-types">
                    <div className="col-md-6">
                        <CostUnitList
                            translate={this.props.translate}
                            costUnitTypes={this.props.costUnitTypes}
                            selectedCostUnitTypes={this.props.selectedCostUnitTypes}
                            onClick={this.selectCostUnitItem}
                            placement='left'
                            type='available'
                            sortable={false}
                            helperClass={'list-group-item-sortable list-group-item-sortable-left'}
                            lockAxis={'y'}
                            useDragHandle
                            systemViewerOnly={this.props.systemViewerOnly}
                        />
                    </div>
                    <div className="col-md-6">
                        <CostUnitList
                            translate={this.props.translate}
                            costUnitTypes={this.props.selectedCostUnitTypes}
                            selectedCostUnitTypes={this.props.costUnitTypes}
                            onClick={this.unselectCostUnitItem}
                            placement='right'
                            type='selected'
                            onSortEnd={this.onSortEnd}
                            sortable={!this.props.systemViewerOnly}
                            distance={1}
                            helperClass={'list-group-item-sortable list-group-item-sortable-right'}
                            lockAxis={'y'}
                            useDragHandle
                            systemViewerOnly={this.props.systemViewerOnly}
                        />
                    </div>
                </div>
                <div className="row" id="cost-unit-types-panel">
                    <div className="col-md-6 offset-md-6">
                        <div className="panel panel-default">
                            <div className="panel-heading">
                                <span className="group-title">
                                    {this.props.translate("costUnitTypes.panelHeader")}
                                </span>
                            </div>
                            <div className="panel-body">
                                {this.renderCurrentlySelectedPanel()}
                            </div>
                        </div>
                    </div>
                </div>
                {this.props.systemViewerOnly ? null :
                    <SettingsButtonsBarView
                        containerInnerStyling={"container"}
                        okCallback={this.onClickSave}
                        cancelEnabled={this.state.costUnitModified}
                        okEnabled={this.state.costUnitModified}
                        cancelCallback={this.props.getCostUnitsData}/>
                }
            </div>
        );
    }
}

const mapStateToProps = function (store) {
    return {
        costUnitTypesVersion: getCostUnitTypesVersion(store)
    }
}
const withTranslations = translate(CostUnitTypes);
const Connected = connect(mapStateToProps, {})(withTranslations);

const CostUnitTypesWithModal = (props) => {
    const useNewModals = launchDarkly.companyClient?.variation("use-new-modals", false);
    const modalContext = useModalContext();
    const {toggleModal} = modalContext || {};
    return <Connected {...props} useNewModals={useNewModals} toggleModal={toggleModal} />;
};

export default CostUnitTypesWithModal;


const CostUnitListItemTitle = SortableHandle((props) =>
    <div className="list-group-item-title">{props.item.name} ({props.item.dimension})</div>
);

const CostUnitListItem = SortableElement((props) =>
    <a className={`list-group-item ${props.item.deleted ? 'deleted' : ''}`}
       onClick={props.systemViewerOnly ? null : props.onClick.bind(null, props.item)}>
        {!props.systemViewerOnly && props.placement !== 'left' &&
            <CircleArrowLeft size={ICON_SIZE.MEDIUM} className="mr-16" aria-hidden="true"/>
        }
        <CostUnitListItemTitle item={props.item}/>
        {!props.systemViewerOnly && props.placement === 'left' &&
            <CircleArrowRight size={ICON_SIZE.MEDIUM} aria-hidden="true"/>
        }
    </a>
);

const CostUnitList = SortableContainer((props) => {
    return (
        <div className={`list-group ${props.placement}`}>
            <div
                className="list-group-item group-title">{props.type === 'available' ? props.translate("costUnitTypes.available") : props.translate("costUnitTypes.selected")}</div>
            {props.costUnitTypes.map((item, index) => {
                return (
                    <CostUnitListItem key={`${item.name}-${item.dimension}`}
                                      disabled={!props.sortable}
                                      onClick={props.onClick}
                                      index={index}
                                      item={item}
                                      placement={props.placement}
                                      systemViewerOnly={props.systemViewerOnly}/>
                );
            })}
            {props.selectedCostUnitTypes.length > props.costUnitTypes.length ?
                [...Array(props.selectedCostUnitTypes.length - props.costUnitTypes.length)].map((item, index) =>
                    <div className="list-group-item ghost-item" key={"ghost-item_" + index}></div>
                ) :
                null}
        </div>
    );
});





